import React, { useContext, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import { useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { getCollectionsSelectedLocation } from '../../navigation/components/CollectionsLocationToggle';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { useDepartmentAssignments, departmentAssignmentsUrl } from '../hooks/useDepartmentAssignments';
import { useAssignableUsers } from '../hooks/useAssignableUsers';
import { useAssignUser, assignUserVariables } from '../hooks/useAssignUser';
import { useRemoveUser, removeUserVariables } from '../hooks/useRemoveUser';
import { AssignableUser, DepartmentAssignment } from '../model';
import { buildGroupOverview } from '../utils/groupOverview';
import GroupOverview from '../components/GroupOverview';

const assignableUserLabel = (user: AssignableUser): string =>
  user.current_department ? `${user.full_name} (currently: ${user.current_department.name})` : user.full_name;

const filterAssignableUsers = (options: AssignableUser[], inputValue: string): AssignableUser[] => {
  const query = inputValue.trim().toLowerCase();
  if (!query) return options;
  return options.filter((user) => assignableUserLabel(user).toLowerCase().includes(query));
};

const sectionTitleSx = {
  color: 'primary.main',
  fontWeight: 600,
  fontSize: '14px',
} as const;

const accordionSx = {
  mb: 1,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'visible',
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    borderColor: 'primary.light',
    bgcolor: 'grey.50',
  },
} as const;

const DepartmentGroupConfigPage: React.FC = () => {
  const [location] = useState(() => getCollectionsSelectedLocation());
  const queryClient = useQueryClient();
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const { data: deptResponse, isLoading } = useDepartmentAssignments(location);
  const { data: usersResponse } = useAssignableUsers(location);
  const assignUser = useAssignUser();
  const removeUser = useRemoveUser();

  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [pickerKeys, setPickerKeys] = useState<Record<number, number>>({});

  const bumpPicker = (departmentId: number) => {
    setPickerKeys((prev) => ({ ...prev, [departmentId]: (prev[departmentId] ?? 0) + 1 }));
  };

  const departments: DepartmentAssignment[] = deptResponse?.data ?? [];
  const assignableUsers: AssignableUser[] = useMemo(() => usersResponse?.data ?? [], [usersResponse]);
  const departmentsWithGroups = useMemo(
    () => departments.filter((department) => department.groups.length > 0),
    [departments],
  );
  const groupOverview = useMemo(() => buildGroupOverview(departments), [departments]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: [departmentAssignmentsUrl(), { location }] });
    await queryClient.invalidateQueries({ queryKey: ['/collections/assignable_users', { location }] });
  };

  const handleAssign = async (departmentId: number, user: AssignableUser | null) => {
    if (!user) return;
    setBusyUserId(user.id);
    try {
      await assignUser.mutateAsync(assignUserVariables(user.id, departmentId));
      await refresh();
      bumpPicker(departmentId);
      const moved = user.current_department && user.current_department.id !== departmentId;
      showSuccessSnackBar?.(moved ? `Moved ${user.full_name}` : `Added ${user.full_name}`);
    } catch (e) {
      showErrorSnackBar?.('Could not assign user');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleRemove = async (userId: number, fullName: string) => {
    setBusyUserId(userId);
    try {
      await removeUser.mutateAsync(removeUserVariables(userId));
      await refresh();
      showSuccessSnackBar?.(`Removed ${fullName}`);
    } catch (e) {
      showErrorSnackBar?.('Could not remove user');
    } finally {
      setBusyUserId(null);
    }
  };

  if (isLoading) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <PageHeader mainTitle="Collections — Departments" titleIcon={<GroupsIcon />} />
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress color="primary" />
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Collections — Departments" titleIcon={<GroupsIcon />} />
      </Grid>

      <Grid item xs={12} md={7} sx={{ mt: 2, pr: { md: 1.5 } }}>
        <Typography variant="h6" sx={sectionTitleSx} gutterBottom>
          Departments
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Expand a department to assign users. Each user covers every group linked to that department.
        </Typography>
        <Divider sx={{ mb: 2, borderColor: 'primary.light', opacity: 0.5 }} />

        <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider' }}>
          {departmentsWithGroups.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No departments with groups for this location.
            </Typography>
          )}
          {departmentsWithGroups.map((dept) => (
            <Accordion key={dept.id} disableGutters sx={accordionSx}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                sx={{
                  borderLeft: '3px solid',
                  borderLeftColor: 'primary.main',
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    my: 1,
                  },
                }}
              >
                <Typography sx={{ fontWeight: 600, mr: 0.5 }}>
                  {dept.name || dept.code}
                  {dept.inactive ? (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                      (inactive)
                    </Typography>
                  ) : null}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                  {dept.groups.map((group) => (
                    <Chip key={group.id} label={group.group_id} size="small" color="primary" variant="outlined" />
                  ))}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', mr: 1, whiteSpace: 'nowrap' }}>
                  <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    {dept.users.length}
                  </Box>{' '}
                  users
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ overflow: 'visible', pt: 0 }}>
                <List dense disablePadding>
                  {dept.users.map((user) => (
                    <ListItem
                      key={user.id}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        bgcolor: 'grey.50',
                        borderLeft: '2px solid',
                        borderLeftColor: 'primary.light',
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          disabled={busyUserId === user.id}
                          onClick={() => handleRemove(user.id, user.full_name)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={user.full_name}
                        secondary={user.email}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Autocomplete
                  key={`add-user-${dept.id}-${pickerKeys[dept.id] ?? 0}`}
                  options={assignableUsers}
                  getOptionLabel={assignableUserLabel}
                  isOptionEqualToValue={(option, value) => value != null && option.id === value.id}
                  value={null}
                  blurOnSelect
                  filterOptions={(options, state) => filterAssignableUsers(options, state.inputValue)}
                  onChange={(_event, value) => handleAssign(dept.id, value)}
                  ListboxProps={{ style: { maxHeight: 280 } }}
                  componentsProps={{
                    popper: {
                      placement: 'bottom-start',
                      style: { zIndex: 1500 },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Add user" size="small" color="primary" autoComplete="off" />
                  )}
                  sx={{ maxWidth: 420, mt: 1 }}
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      </Grid>

      <Grid item xs={12} md={5} sx={{ mt: 2, pl: { md: 1.5 } }}>
        <Box sx={{ position: { md: 'sticky' }, top: 16 }}>
          <Typography variant="h6" sx={sectionTitleSx} gutterBottom>
            Department Groups
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Summary of groups and how many users are assigned across departments.
          </Typography>
          <Divider sx={{ mb: 2, borderColor: 'primary.light', opacity: 0.5 }} />

          <Box
            sx={{
              '& .MuiCard-root': {
                borderLeft: '3px solid',
                borderLeftColor: 'primary.main',
              },
            }}
          >
            <GroupOverview items={groupOverview} />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default DepartmentGroupConfigPage;
